import type { MetadataAuthorAccordionItemFragment } from "../client/generated";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { MetadataAuthorInput } from "./MetadataAuthorInput";
import { AccessButton } from "@acdc2/ui/components/access-button";
import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@acdc2/ui/components/accordion";
import { AccessSeparator } from "@acdc2/ui/components/access-separator";
import { v4 as uuid } from "uuid";
import {
  useAddAuthorMutation,
  useRemoveAuthorMutation,
  useRenameAuthorMutation,
} from "../client/generated";
type Props = {
  fragment: MetadataAuthorAccordionItemFragment;
};
type UnsavedAuthor = {
  id: string;
  name?: string;
};

export function MetadataAuthorAccordionItem({ fragment }: Props) {
  const { t } = useTranslation();

  const [unsavedAuthors, setUnsavedAuthors] = useState<UnsavedAuthor[]>([]);

  const [addAuthor] = useAddAuthorMutation({
    refetchQueries: "active",
  });
  const [renameAuthor] = useRenameAuthorMutation();
  const [removeAuthor] = useRemoveAuthorMutation({
    update(cache) {
      const id = cache.identify(fragment);
      cache.evict({ id });
      cache.gc();
    },
  });

  const createUnsavedAuthor = () => {
    setUnsavedAuthors((oldAuthors) => [...oldAuthors, { id: uuid() }]);
  };

  const removeUnsavedAuthor = (authorId: string) => {
    setUnsavedAuthors((oldAuthors) =>
      oldAuthors.filter((author) => author.id !== authorId),
    );
  };

  const createSavedAuthor = async (authorId: string, newAuthorName: string) => {
    const result = await addAuthor({
      variables: {
        documentId: fragment.id,
        name: newAuthorName,
      },
    });
    if (result.data?.addAuthor.author) {
      removeUnsavedAuthor(authorId);
    }
  };

  const onSave = async (id: string, newAuthor: string) => {
    await renameAuthor({
      variables: {
        authorId: id,
        name: newAuthor,
      },
    });
  };

  const removeSavedAuthor = async (authorId: string) => {
    const result = await removeAuthor({ variables: { authorId } });
    if (result.data?.removeAuthor.author) {
      setUnsavedAuthors((oldAuthors) =>
        oldAuthors.filter((author) => author.id !== authorId),
      );
    }
  };

  return (
    <AccordionItem value="authors">
      <AccordionTrigger className="px-4">
        {t("MetadataAuthorAccordionItem.label")}
      </AccordionTrigger>
      <AccordionContent className="px-4 pt-1 flex flex-col gap-2">
        {fragment.metadata.authors.map((author) => (
          <MetadataAuthorInput
            defaultValue={author.name}
            key={author.id}
            onDelete={() => removeSavedAuthor(author.id)}
            onSave={(newAuthor: string) => onSave(author.id, newAuthor)}
          />
        ))}

        {unsavedAuthors.length > 0 && (
          <AccessSeparator
            className="my-1"
            title={t("MetadataAuthorAccordionItem.separator")}
          />
        )}
        {unsavedAuthors.map((author) => (
          <MetadataAuthorInput
            key={author.id}
            defaultValue={author.name ?? ""}
            onDelete={() => removeUnsavedAuthor(author.id)}
            onSave={(newAuthor) => {
              createSavedAuthor(author.id, newAuthor);
            }}
          />
        ))}

        <AccessButton type="button" onClick={() => createUnsavedAuthor()}>
          {t("MetadataAuthorAccordionItem.add")}
        </AccessButton>
      </AccordionContent>
    </AccordionItem>
  );
}
